/**
 * Настройки диалогом (ТЗ UI-34).
 *
 * До этого была раскрывающаяся панель: она открывалась поверх содержимого, но
 * фокус из неё уходил на страницу под ней. Человек, работающий с клавиатуры,
 * нажимал Tab и оказывался в тексте, который панель закрывает собой, — и
 * возвращался наугад.
 *
 * Диалог держит фокус внутри, закрывается по Escape и возвращает фокус той
 * кнопке, которая его открыла. Всё это делается руками, а не элементом
 * `<dialog>`: `showModal` в jsdom не реализован, и проверить поведение было бы
 * нечем — а непроверяемое поведение здесь как раз то, ради чего этап и делался.
 */

import { useEffect, useRef } from 'react';
import type { Translate } from '@type2x/i18n';
import { Button } from '@type2x/ui';

/** Что вообще может получить фокус внутри диалога. */
const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

export interface SettingsDialogProps {
  readonly t: Translate;
  readonly titleId: string;
  readonly onClose: () => void;
  readonly children: React.ReactNode;
}

export function SettingsDialog({
  t,
  titleId,
  onClose,
  children,
}: SettingsDialogProps): React.JSX.Element {
  const panel = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);

  // Фокус переходит на заголовок: он называет, куда человек попал, до того
  // как тот начнёт перебирать элементы.
  useEffect(() => {
    heading.current?.focus();
  }, []);

  /**
   * Ловушка фокуса.
   *
   * Tab с последнего элемента возвращает на первый, Shift+Tab с первого — на
   * последний. Без этого следующий Tab уводит на страницу под диалогом, и
   * человек управляет тем, чего не видит.
   */
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      onClose();
      return;
    }
    if (event.key !== 'Tab') return;

    const items = [...(panel.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [])];
    if (items.length === 0) return;

    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;

    if (event.shiftKey && (active === first || active === heading.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      className="t2x-dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      ref={panel}
      onKeyDown={onKeyDown}
    >
      <div className="t2x-dialog__head">
        <h2 className="t2x-dialog__title" id={titleId} tabIndex={-1} ref={heading}>
          {t('settings.title')}
        </h2>
        <Button variant="ghost" onClick={onClose}>
          {t('settings.close')}
        </Button>
      </div>

      {children}
    </div>
  );
}
